import React, { useState, useEffect } from "react";
import { ShowButton } from "@refinedev/antd";
import { useList } from "@refinedev/core";
import { Table, Space, Tag, Tooltip, Input, Button, Card } from "antd";
import { SearchOutlined, PrinterOutlined, PlusOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../../components/ui";

interface Purchase {
    id: number;
    orderNumber: string;
    status: string;
    totalAmount: number;
    currencyTypeCode: string;
    createdAt: string;
    vendor: { id: number; name: string; tpin?: string };
    documentUrl?: string;
}

export const PurchaseList = () => {
    const [searchText, setSearchText] = useState<string>("");
    const [filteredItems, setFilteredItems] = useState<Purchase[]>([]);
    const navigate = useNavigate();

    const { data, isLoading } = useList({
        resource: "purchases",
    });

    useEffect(() => {
        if (!data?.data) return;

        let purchases = data.data as Purchase[];
        
        if (searchText.trim() !== "") {
            const searchLower = searchText.toLowerCase();
            purchases = purchases.filter((purchase: Purchase) =>
                purchase.orderNumber?.toLowerCase().includes(searchLower) ||
                purchase.vendor?.name?.toLowerCase().includes(searchLower) ||
                purchase.status?.toLowerCase().includes(searchLower)
            );
        }

        const sorted = [...purchases].sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setFilteredItems(sorted);
    }, [data, searchText]);

    const handlePrint = (record: Purchase) => {
        if (record?.documentUrl) {
            window.open(record.documentUrl, '_blank');
        }
    };

    const columns = [
        {
            title: "Order #",
            dataIndex: "orderNumber",
            key: "orderNumber",
            render: (value: string) => (
                <span className="font-medium text-text-primary">{value || "-"}</span>
            ),
            sorter: (a: Purchase, b: Purchase) => (a.orderNumber || "").localeCompare(b.orderNumber || ""),
        },
        {
            title: "Vendor",
            key: "vendor",
            render: (_: any, record: Purchase) => (
                <span className="text-text-secondary">{record.vendor?.name || "N/A"}</span>
            ),
            sorter: (a: Purchase, b: Purchase) => (a.vendor?.name || "").localeCompare(b.vendor?.name || ""),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (value: string) => (
                <Tag color={value === "Completed" ? "success" : "warning"} className="status-tag">
                    {value}
                </Tag>
            ),
        },
        {
            title: "Amount",
            key: "amount",
            render: (_: any, record: Purchase) => (
                <span className="font-semibold text-primary">
                    {record.currencyTypeCode} {record.totalAmount?.toFixed(2) || '0.00'}
                </span>
            ),
            sorter: (a: Purchase, b: Purchase) => (a.totalAmount || 0) - (b.totalAmount || 0),
        },
        {
            title: "Created",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {value ? new Date(value).toLocaleDateString() : "-"}
                </span>
            ),
            sorter: (a: Purchase, b: Purchase) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
            responsive: ['md'] as any,
        },
        {
            title: "Actions",
            key: "actions",
            width: 100,
            render: (_: any, record: Purchase) => (
                <Space size="small">
                    <ShowButton hideText size="small" recordItemId={record.id} />
                    {record?.documentUrl && record?.status === "Completed" && (
                        <Tooltip title="Print">
                            <Button size="small" icon={<PrinterOutlined />} onClick={() => handlePrint(record)} />
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title="Purchases"
                icon={<ShoppingCartOutlined />}
                breadcrumbs={[{ title: "Purchases" }]}
                actions={[
                    {
                        label: "Create",
                        onClick: () => navigate("/purchases/create"),
                        type: "primary",
                        icon: <PlusOutlined />,
                    },
                ]}
            />

            <div className="data-table-card">
                <Card>
                    <div style={{ marginBottom: 16 }}>
                        <Input
                            placeholder="Search by order number, vendor, or status"
                            prefix={<SearchOutlined className="text-text-muted" />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ maxWidth: 400 }}
                            allowClear
                            size="large"
                        />
                    </div>

                    <Table
                        dataSource={filteredItems}
                        columns={columns}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        }}
                        className="custom-table"
                        scroll={{ x: 800 }}
                    />
                </Card>
            </div>
        </div>
    );
};
